import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { SignOutButton } from "./SignOutButton";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { Wordmark } from "./Wordmark";

/**
 * The 48px white bar: wordmark (to the dashboard), the Book title (to its
 * overview), the steps, DE|EN, sign-out. Screens outside a Book leave
 * `title` and `steps` empty.
 */
export function TopNav({
  title,
  titleTo,
  steps,
  right,
}: {
  title?: ReactNode;
  titleTo?: string;
  steps?: ReactNode;
  /** Replaces the default DE|EN plus sign-out. */
  right?: ReactNode;
}) {
  const { t } = useTranslation(["nav", "components"]);
  return (
    <header
      data-component="TopNav"
      className="grid min-h-nav shrink-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 border-b border-line bg-surface px-4 py-2 xl:flex xl:h-nav xl:gap-5 xl:px-5 xl:py-0"
    >
      <Link to="/" title={t("nav:home")} className="shrink-0">
        <Wordmark />
      </Link>
      {title ? (
        titleTo ? (
          <Link
            to={titleTo}
            title={t("components:nav.toOverview")}
            className="min-w-0 truncate text-ui text-ink-2 hover:text-ink"
          >
            {title}
          </Link>
        ) : (
          <span className="min-w-0 truncate text-ui text-ink-2">{title}</span>
        )
      ) : null}
      <div className="hidden xl:block xl:flex-1" />
      {steps ? (
        <div className="relative col-span-3 row-start-3 min-w-0 sm:row-start-2 overflow-x-auto pb-1 xl:pb-0">
          {steps}
        </div>
      ) : null}
      <div className="col-span-3 row-start-2 flex shrink-0 flex-wrap items-center justify-end gap-3 sm:col-span-1 sm:col-start-3 sm:row-start-1">
        {right ?? (
          <>
            <ThemeSwitcher />
            <LanguageSwitcher />
            <SignOutButton />
          </>
        )}
      </div>
    </header>
  );
}
